import { storage } from "./storage";
import { ROLES } from "./auth";

/**
 * Synchronous bootstrap. Reads existing localStorage state, performs idempotent
 * migrations, and returns whatever is currently persisted.
 *
 * IMPORTANT: This function does NOT seed default teachers anymore. On a fresh
 * install (`teachers.length === 0`), the UI must route the user through the
 * `/setup` first-launch flow to create the Studio Admin (and optionally a
 * first teacher) before entering the app.
 *
 * TODO(supabase-auth): replace this whole function with a Supabase session
 *   bootstrap (getSession, ensure teacher row exists in `teachers` table).
 */
export function bootstrap() {
    let teachers = storage.getTeachers();

    // Migrate any legacy teacher records to the `status` model. Old format used
    // `active: boolean`; new uses `status: 'active' | 'inactive'`. Also rebrand
    // legacy `@funshop.studio` emails to `@tempo.studio`.
    let teachersMigrated = false;
    teachers = teachers.map((t) => {
        let next = t;
        if (next.status === undefined) {
            teachersMigrated = true;
            const status = next.active === false ? "inactive" : "active";
            next = { ...next, status };
            delete next.active;
        }
        if (next.email && next.email.endsWith("@funshop.studio")) {
            teachersMigrated = true;
            next = {
                ...next,
                email: next.email.replace("@funshop.studio", "@tempo.studio"),
            };
        }
        return next;
    });
    if (teachersMigrated) storage.saveTeachers(teachers);

    const firstTeacher =
        teachers.find((t) => t.role === ROLES.TEACHER) || teachers[0] || null;

    let students = storage.getStudents();
    let attendance = storage.getAttendance();

    // Migration: any pre-existing record without teacherId gets stamped onto
    // the first teacher's workspace so legacy data isn't lost. (Skipped on
    // fresh installs where there are no students yet.)
    let migrated = false;
    const stampedStudents = students.map((s) => {
        if (!s.teacherId && firstTeacher) {
            migrated = true;
            return { ...s, teacherId: firstTeacher.id };
        }
        return s;
    });
    const stampedAttendance = attendance.map((r) => {
        if (!r.teacherId && firstTeacher) {
            migrated = true;
            return { ...r, teacherId: firstTeacher.id };
        }
        return r;
    });
    if (migrated) {
        storage.saveStudents(stampedStudents);
        storage.saveAttendance(stampedAttendance);
    }

    // Active user defaults to the first ACTIVE teacher; clamp on every reload.
    let activeUserId = storage.getActiveUserId();
    const isValidActive =
        activeUserId &&
        teachers.some(
            (t) => t.id === activeUserId && t.status !== "inactive",
        );
    if (!isValidActive) {
        const fallback =
            teachers.find(
                (t) => t.role === ROLES.TEACHER && t.status !== "inactive",
            ) ||
            teachers.find(
                (t) => t.role === ROLES.ADMIN && t.status !== "inactive",
            ) ||
            firstTeacher;
        activeUserId = fallback ? fallback.id : null;
        if (activeUserId) storage.setActiveUserId(activeUserId);
    }

    const workspaceMode = (() => {
        const stored = storage.getWorkspaceMode();
        if (stored === null || stored === undefined || stored === "") {
            storage.setWorkspaceMode("all");
            return "all";
        }
        return stored;
    })();

    return {
        teachers,
        activeUserId,
        workspaceMode,
        students: stampedStudents,
        attendance: stampedAttendance,
        needsSetup: teachers.length === 0,
    };
}
